import React from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, Heart, Shield } from 'lucide-react';
import { useApp } from '../../context/AppContext';

const Footer = () => {
  const { setCurrentView } = useApp();
  
  const currentYear = new Date().getFullYear();
  
  const footerLinks = [
    {
      title: 'Other VIBE Edition Apps',
      links: [
        { name: 'Story Threads Vibe', href: 'https://story-threads-vibe.divedeepdialogues.com', external: true },
        { name: 'MindShield Vibe', href: '#', disabled: true },
      ]
    },
        {
      title: 'Some of Our Other Apps',
      links: [
        { name: 'Dive Deep Workplace', href: 'https://divedeepworkplace.com', external: true },
        { name: 'Dive Deep Dating', href: 'https://divedeepdating.com', external: true },
        { name: 'Inner Voice', href: 'https://innervoice.divedeepdialogues.com', external: true },
        { name: 'Deeper Dialogues (Soon)', href: '#', disabled: true }
      ]
    },
    {
      title: 'Legal',
      links: [
        { name: 'Privacy Policy', action: () => setCurrentView('privacy-policy'), internal: true },
        // The terms are DDD-wide, not per app: link to the Dive Deep Dialogues terms page.
        { name: 'Terms & Conditions', href: 'https://divedeepdialogues.com/terms', external: true }
      ]
    },
    {
      title: 'About',
      links: [
        { name: 'Dive Deep Dialogues', href: 'https://divedeepdialogues.com', external: true, logo: true },
        { name: 'Contact', href: 'mailto:help@divedeepdialogues.com', external: true }
      ]
    }
  ];

  return (
    <footer className="relative mt-auto">
      <div className="bg-slate-900 border-t border-purple-500/20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 max-w-7xl">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Brand Section */}
            <div className="space-y-4">
              <div className="space-y-3">
                {/* Friendships Vibe Logo & Title */}
                <div className="flex items-center gap-3">
                  <img
                    src="/friendships-vibe-logo-150px.png"
                    alt="Friendships Vibe Logo"
                    className="w-12 h-12"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  <div>
                    <h3 className="text-xl font-bold text-white">Friendships Vibe</h3>
                    <p className="text-xs text-purple-300">A <span className="font-bold">Dive Deep Dialogues</span> App</p>
                  </div>
                </div>
                
                {/* App Series Text */}
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <span>Part of the</span>
                  <a 
                    href="https://divedeepdialogues.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-purple-400 hover:text-purple-300 transition-colors inline-flex items-center gap-1"
                  >
                    Dive Deep into Self
                    <ExternalLink className="w-3 h-3" />
                  </a>
                  <span>app series</span>
                </div>
              </div>
              
              <p className="text-sm text-slate-400 leading-relaxed">
                Transform your friendship patterns with science-backed insights and personalised guidance.
              </p>
            </div>

            {/* Links Sections */}
            {footerLinks.map((section, index) => (
              <div key={index} className="space-y-4">
                <h4 className="text-sm font-semibold text-white uppercase tracking-wider">
                  {section.title}
                </h4>
                <ul className="space-y-2">
                  {section.links.map((link, linkIndex) => (
                    <li key={linkIndex}>
                      {link.disabled ? (
                        <span className="text-sm text-slate-500 cursor-not-allowed inline-flex items-center gap-1">
                          {link.name}
                        </span>
                      ) : link.internal ? (
                        <button
                          onClick={link.action}
                          className="text-sm text-slate-400 hover:text-purple-300 transition-colors inline-flex items-center gap-1"
                        >
                          {link.name}
                        </button>
                      ) : (
                        <a
                          href={link.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-slate-400 hover:text-purple-300 transition-colors inline-flex items-center gap-2"
                        >
                          {link.logo && (
                            <img 
                              src="/ddd-logo.svg" 
                              alt="Dive Deep Dialogues Logo" 
                              className="w-12 h-12"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          )}
                          {link.name}
                          {link.external && <ExternalLink className="w-3 h-3" />}
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            {/* Made with Love */}
            <div className="space-y-4 text-left">
              <div className="text-sm text-slate-400">
                <p className="mb-2">Check out our <a href="https://inner-voice-vibe.divedeepdialogues.com" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:text-purple-300 transition-colors font-bold">Inner Voice Vibe Edition</a> for comprehensive personal development</p>
                <div className="flex items-center justify-start gap-2">
                  <span>Made with</span>
                  <Heart className="w-4 h-4 text-red-400 fill-current" />
                  <span>by</span>
                  <a 
                    href="https://divedeepdialogues.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-purple-400 hover:text-purple-300 transition-colors"
                  >
                    Dive Deep Dialogues
                  </a>
                </div>
              </div>
            </div>
                        {/* Made with Love */}
            <div className="space-y-4 text-left">
              <div className="text-sm text-slate-400">
                <p className="mb-2">This app was inspired by: </p>
                <div className="flex items-center justify-start gap-2">
                  
                  <p className="text-purple-400 hover:text-purple-300 transition-colors font-bold"><span>Piper</span> </p>
                  <Shield className="w-4 h-4 text-red-400 fill-current" />
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="mt-8 pt-6 border-t border-slate-800">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <p className="text-xs text-slate-500">
                © {currentYear} SILVER WOLF Productions. All rights reserved.
              </p>
              <div className="flex items-center gap-4 text-xs text-slate-500">
                <div className="flex items-center gap-1">
                  <Shield className="w-3 h-3" />
                  <span>No ads, and your data is never sold</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;